import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from '@mui/material';
import {
  ArrowBack,
  Print as PrintIcon,
  Download,
  Preview
} from '@mui/icons-material';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import apiService from '../services/api';
import { Member } from '../types';

interface ContractData {
  member: Member;
  gymInfo: {
    name: string;
    address: string;
    phone: string;
    email: string;
    website: string;
  };
  contractTerms: string[];
  settings: {
    defaultMonthlyFee: number;
    defaultJoiningFee: number;
  };
}

const PrintContract: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contractData, setContractData] = useState<ContractData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  console.log('PrintContract component loaded with ID:', id);

  useEffect(() => {
    const fetchContractData = async () => {
      if (!id) {
        console.log('No member ID provided - using test data or redirecting');
        setError('No member ID provided. Please access this page from a member profile.');
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching contract data for member:', id);
        const contractData = await apiService.get(`/api/members/${id}/contract-data`);
        console.log('Contract data received:', contractData);
        setContractData(contractData);
      } catch (err: any) {
        console.error('Error fetching contract data:', err);
        setError(err.response?.data?.message || 'Failed to load contract data');
      } finally {
        setLoading(false);
      }
    };

    fetchContractData();
  }, [id]);

  const generatePDF = () => {
    if (!contractData) return;

    const doc = new jsPDF();
    const { member } = contractData;
    
    // Set font
    doc.setFont('helvetica');
    
    // Header - Legends Fitness Centre
    doc.setFontSize(24);
    doc.setTextColor(0, 0, 0);
    doc.text('LEGENDS FITNESS CENTRE', 105, 25, { align: 'center' });
    
    // Subheader
    doc.setFontSize(12);
    doc.text('MEMBERSHIP CONTRACT', 105, 35, { align: 'center' });
    
    // Member Details Section
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('MEMBER DETAILS', 20, 55);
    
    // Draw a line under the title
    doc.setLineWidth(0.5);
    doc.line(20, 57, 190, 57);
    
    let yPos = 70;
    
    // Member Information Form Fields
    doc.setFontSize(10);
    
    // Row 1: Name and Surname
    doc.text('Name:', 20, yPos);
    doc.text(member.firstName || '', 45, yPos);
    doc.text('Surname:', 120, yPos);
    doc.text(member.lastName || '', 145, yPos);
    
    // Draw lines for form fields
    doc.line(45, yPos + 2, 115, yPos + 2);
    doc.line(145, yPos + 2, 190, yPos + 2);
    
    yPos += 15;
    
    // Row 2: ID Number and Date of Birth
    doc.text('ID Number:', 20, yPos);
    doc.text(member.idNumber || '', 50, yPos);
    doc.text('Date of Birth:', 120, yPos);
    doc.text(member.dateOfBirth ? format(new Date(member.dateOfBirth), 'dd/MM/yyyy') : '', 155, yPos);
    
    doc.line(50, yPos + 2, 115, yPos + 2);
    doc.line(155, yPos + 2, 190, yPos + 2);
    
    yPos += 15;
    
    // Row 3: Email and Phone
    doc.text('Email:', 20, yPos);
    doc.text(member.email || '', 40, yPos);
    doc.text('Phone:', 120, yPos);
    const phoneText = typeof member.phone === 'string' ? member.phone : 
                     (member.phone?.mobile || member.phone?.work || '');
    doc.text(phoneText, 140, yPos);
    
    doc.line(40, yPos + 2, 115, yPos + 2);
    doc.line(140, yPos + 2, 190, yPos + 2);
    
    yPos += 15;
    
    // Address
    doc.text('Address:', 20, yPos);
    const addressText = `${member.address?.street || ''}, ${member.address?.city || ''}, ${member.address?.zipCode || ''}`;
    doc.text(addressText, 45, yPos);
    doc.line(45, yPos + 2, 190, yPos + 2);
    
    yPos += 20;
    
    // Emergency Contact Section
    doc.setFontSize(14);
    doc.text('EMERGENCY CONTACT', 20, yPos);
    doc.line(20, yPos + 2, 190, yPos + 2);
    
    yPos += 15;
    doc.setFontSize(10);
    
    doc.text('Name:', 20, yPos);
    const emergencyContact = (member as any).emergencyContact;
    doc.text(emergencyContact?.name || '', 45, yPos);
    doc.text('Relationship:', 120, yPos);
    doc.text(emergencyContact?.relationship || '', 155, yPos);
    
    doc.line(45, yPos + 2, 115, yPos + 2);
    doc.line(155, yPos + 2, 190, yPos + 2);
    
    yPos += 15;
    
    doc.text('Phone:', 20, yPos);
    doc.text(emergencyContact?.phone || '', 45, yPos);
    doc.line(45, yPos + 2, 115, yPos + 2);
    
    yPos += 20;
    
    // Membership Details Section
    doc.setFontSize(14);
    doc.text('MEMBERSHIP DETAILS', 20, yPos);
    doc.line(20, yPos + 2, 190, yPos + 2);
    
    yPos += 15;
    doc.setFontSize(10);
    
    doc.text('Membership Type:', 20, yPos);
    doc.text(member.membershipType || '', 70, yPos);
    doc.text('Start Date:', 120, yPos);
    doc.text(member.startDate ? format(new Date(member.startDate), 'dd/MM/yyyy') : '', 155, yPos);
    
    doc.line(70, yPos + 2, 115, yPos + 2);
    doc.line(155, yPos + 2, 190, yPos + 2);
    
    yPos += 15;
    
    doc.text('Monthly Fee:', 20, yPos);
    doc.text(`R ${member.rate || 0}`, 55, yPos);
    doc.text('Payment Day:', 120, yPos);
    doc.text(member.paymentDay || '', 155, yPos);
    
    doc.line(55, yPos + 2, 115, yPos + 2);
    doc.line(155, yPos + 2, 190, yPos + 2);
    
    yPos += 15;
    
    doc.text('Joining Fee:', 20, yPos);
    doc.text(`R ${member.joiningFee || 0}`, 55, yPos);
    doc.text('Payment Method:', 120, yPos);
    doc.text(member.paymentFrequency || 'Monthly', 170, yPos);
    
    doc.line(55, yPos + 2, 115, yPos + 2);
    doc.line(170, yPos + 2, 190, yPos + 2);
    
    yPos += 25;
    
    // Terms and Conditions Section
    doc.setFontSize(14);
    doc.text('TERMS AND CONDITIONS', 20, yPos);
    doc.line(20, yPos + 2, 190, yPos + 2);
    
    yPos += 10;
    doc.setFontSize(8);
    
    const terms = [
      '1. This membership is non-transferable and non-refundable except as required by law.',
      '2. Member must provide 30-day written notice for cancellation of membership.',
      '3. Monthly fees are due on the specified payment date and late fees may apply.',
      '4. Member must present valid ID and membership card for facility access.',
      '5. Member acknowledges that physical exercise involves risk and releases the gym from liability.',
      '6. Member agrees to follow all gym rules and regulations as posted.',
      '7. Gym reserves the right to suspend or terminate membership for violation of rules or non-payment.',
      '8. Member is responsible for any damage to equipment caused by misuse or negligence.',
      '9. Membership includes access to general gym facilities only unless otherwise specified.',
      '10. Member agrees to conduct themselves in a respectful manner towards staff and other members.'
    ];
    
    terms.forEach((term) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 30;
      }
      
      const lines = doc.splitTextToSize(term, 170);
      lines.forEach((line: string) => {
        doc.text(line, 20, yPos);
        yPos += 4;
      });
      yPos += 2;
    });
    
    // Add new page for signatures if needed
    if (yPos > 220) {
      doc.addPage();
      yPos = 50;
    } else {
      yPos += 20;
    }
    
    // Signatures Section
    doc.setFontSize(14);
    doc.text('SIGNATURES', 20, yPos);
    doc.line(20, yPos + 2, 190, yPos + 2);
    
    yPos += 25;
    doc.setFontSize(10);
    
    // Member signature
    doc.text('Member Signature:', 20, yPos);
    doc.text('Date:', 120, yPos);
    doc.text(format(new Date(), 'dd/MM/yyyy'), 135, yPos);
    
    doc.line(20, yPos + 15, 110, yPos + 15);
    doc.line(135, yPos + 2, 190, yPos + 2);
    
    yPos += 25;
    doc.text(`${member.firstName} ${member.lastName}`, 20, yPos);
    doc.text('(Print Name)', 20, yPos + 5);
    
    yPos += 20;
    
    // Witness/Staff signature
    doc.text('Witness/Staff Signature:', 20, yPos);
    doc.text('Date:', 120, yPos);
    doc.text(format(new Date(), 'dd/MM/yyyy'), 135, yPos);
    
    doc.line(20, yPos + 15, 110, yPos + 15);
    doc.line(135, yPos + 2, 190, yPos + 2);
    
    yPos += 25;
    doc.text('Authorized Representative', 20, yPos);
    doc.text('(Print Name)', 20, yPos + 5);
    
    // Footer
    yPos += 30;
    if (yPos > 250) {
      doc.addPage();
      yPos = 50;
    }
    
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('This contract is effective from the date signed and is subject to the terms and conditions outlined above.', 105, yPos, { align: 'center' });
    doc.text(`Contract generated on: ${format(new Date(), 'dd MMMM yyyy')}`, 105, yPos + 10, { align: 'center' });

    return doc;
  };

  const handlePrint = () => {
    const doc = generatePDF();
    if (doc) {
      doc.autoPrint();
      window.open(doc.output('bloburl'), '_blank');
    }
  };

  const handleDownload = () => {
    const doc = generatePDF();
    if (doc && contractData) {
      doc.save(`${contractData.member.firstName}_${contractData.member.lastName}_Contract.pdf`);
    }
  };

  const handlePreview = () => {
    const doc = generatePDF();
    if (doc) {
      window.open(doc.output('bloburl'), '_blank');
    }
  };

  if (loading) {
    console.log('PrintContract: Loading contract data...');
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading contract data...</Typography>
      </Box>
    );
  }

  if (error) {
    console.error('PrintContract: Error occurred:', error);
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/members')}
        >
          Back to Members
        </Button>
      </Box>
    );
  }

  if (!contractData) {
    console.log('PrintContract: No contract data available');
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Contract data not found
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/members')}
        >
          Back to Members
        </Button>
      </Box>
    );
  }

  const { member, gymInfo, contractTerms } = contractData;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/members/${id}`)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">
            Contract - {member.firstName} {member.lastName}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Preview />}
            onClick={handlePreview}
            sx={{ mr: 1 }}
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownload}
            sx={{ mr: 1 }}
          >
            Download
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print
          </Button>
        </Box>
      </Box>

      {/* Contract Preview */}
      <Paper sx={{ p: 4, maxWidth: '21cm', mx: 'auto' }}>
        {/* Gym Header */}
        <Box mb={4}>
          <Typography variant="h3" color="primary" gutterBottom>
            {gymInfo.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {gymInfo.address}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Phone: {gymInfo.phone} | Email: {gymInfo.email}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Contract Title */}
        <Typography variant="h4" align="center" gutterBottom>
          MEMBERSHIP CONTRACT
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" paragraph>
          Contract Date: {format(new Date(), 'dd MMMM yyyy')}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Member Information */}
        <Typography variant="h5" gutterBottom>
          Member Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell variant="head">Full Name:</TableCell>
                    <TableCell>{member.firstName} {member.lastName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head">ID Number:</TableCell>
                    <TableCell>{member.idNumber}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head">Email:</TableCell>
                    <TableCell>{member.email}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head">Phone:</TableCell>
                    <TableCell>{member.phone?.mobile || ''}{member.phone?.work ? ` / ${member.phone.work}` : ''}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head">Date of Birth:</TableCell>
                    <TableCell>{member.dateOfBirth ? format(new Date(member.dateOfBirth), 'dd MMMM yyyy') : 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} md={6}>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell variant="head">Membership Type:</TableCell>
                    <TableCell>{member.membershipType}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head">Start Date:</TableCell>
                    <TableCell>{member.startDate ? format(new Date(member.startDate), 'dd MMMM yyyy') : 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head">End Date:</TableCell>
                    <TableCell>{member.expiryDate ? format(new Date(member.expiryDate), 'dd MMMM yyyy') : 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head">Address:</TableCell>
                    <TableCell>
                      {member.address?.street && (
                        <>
                          {member.address.street}
                          {member.address.city && `, ${member.address.city}`}
                          {member.address.zipCode && `, ${member.address.zipCode}`}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Financial Information */}
        <Typography variant="h5" gutterBottom>
          Financial Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell variant="head">Monthly Fee:</TableCell>
                    <TableCell>R {member.rate || contractData.settings.defaultMonthlyFee}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head">Joining Fee:</TableCell>
                    <TableCell>R {member.joiningFee || contractData.settings.defaultJoiningFee}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head">Pro Rata:</TableCell>
                    <TableCell>R {member.proRata || 0}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} md={6}>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell variant="head">Payment Frequency:</TableCell>
                    <TableCell>{member.paymentFrequency || 'Monthly'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head">Payment Day:</TableCell>
                    <TableCell>{member.paymentDay || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Terms and Conditions */}
        <Typography variant="h5" gutterBottom>
          Terms and Conditions
        </Typography>
        <Box sx={{ mb: 4 }}>
          {contractTerms.map((term, index) => (
            <Typography key={index} variant="body2" paragraph>
              {index + 1}. {term}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Signatures */}
        <Typography variant="h5" gutterBottom>
          Signatures
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" gutterBottom>
                Member Signature:
              </Typography>
              <Box sx={{ borderBottom: 1, borderColor: 'grey.400', height: 40, mb: 1 }} />
              <Typography variant="body2">
                {member.firstName} {member.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: {format(new Date(), 'dd/MM/yyyy')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" gutterBottom>
                Gym Representative:
              </Typography>
              <Box sx={{ borderBottom: 1, borderColor: 'grey.400', height: 40, mb: 1 }} />
              <Typography variant="body2">
                Authorized Representative
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: {format(new Date(), 'dd/MM/yyyy')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default PrintContract;
